import React, { useState } from 'react';
import { Link } from 'react-scroll';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { HomeIcon, InfoIcon, SuitcaseIcon, CommentIcon, EmailIcon, CVIcon } from './PortfolioIcons';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLink, setActiveLink] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const shouldReduceMotion = useReducedMotion();
  const scrollTimeoutRef = React.useRef(null);
  const navRef = React.useRef(null);
  const scrollDuration = 180;

  /* Toggle menu visibility */
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  /* Listen for scroll event */
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      /* Set isScrolled to true if user scrolls past 50px */
      setIsScrolled(scrollTop > 50);
      /* Set isScrolling to true and reset timeout for bounce effect */
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  /* Listen for resize event */
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      if (width >= 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /* Close menu when clicking outside */
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const links = [
    { id: 'hero', title: 'home', offset: -95, icon: <HomeIcon />, ref: null },
    { id: 'projects', title: 'projects', offset: -95, icon: <SuitcaseIcon />, ref: null },
    { id: 'about', title: 'about', offset: -95, icon: <InfoIcon />, ref: null },
    { id: 'testimonials', title: 'testimonials', offset: -95, icon: <CommentIcon />, ref: null },
    { id: 'contact', title: 'contact', offset: -95, icon: <EmailIcon />, ref: null },
  ];

  const downloadCvUrl = '/feedback-cv/cv-neka-toni-uebari.pdf';
  const cvButtonClass = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-blue-300/70 bg-gradient-to-r from-blue-500/15 to-cyan-500/10 px-2.5 py-1.5 text-sm font-medium text-blue-100 transition-all duration-200 hover:bg-blue-500 hover:text-white hover:border-blue-300/80 flex-shrink-0';

  return (
    <motion.nav
      ref={navRef}
      className={`bg-black bg-opacity-85 fixed top-0 z-20 md:border-gray-900 m-0 w-full md:m-6 md:inset-x-0 md:mx-auto md:w-[85%] md:rounded-full md:border ${isScrolling ? 'md:shadow-2xl md:shadow-blue-500/20' : 'md:shadow-lg'}`}
      style={{
        backdropFilter: screenWidth >= 768 ? (isScrolling ? 'blur(12px)' : 'blur(2px)') : 'blur(10px)',
        WebkitBackdropFilter: screenWidth >= 768 ? (isScrolling ? 'blur(12px)' : 'blur(2px)') : 'blur(10px)',
      }}
      initial={shouldReduceMotion ? false : { y: -100 }}
      animate={{ y: isScrolled || isOpen ? 0 : -100, scale: screenWidth >= 768 && isScrolling ? 1.02 : 1 }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, scale: { type: 'spring', stiffness: 400, damping: 10 } }}
    >
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex items-center justify-between h-12 gap-3">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to={'hero'}
              spy={true}
              offset={-95}
              smooth={true}
              duration={scrollDuration}
              className="select-none flex-shrink-0 text-white font-bold text-xl
                  hover:text-blue-400 hover:cursor-pointer transition 100">
              toni.
            </Link>
          </div>
          {/* Desktop Menu */}
          <div className="hidden md:flex flex-grow justify-end">
            <div className="flex items-center gap-2 lg:gap-3">
              {links.map(({ id, title, offset, icon, ref }) => (
                <Link
                  key={id}
                  to={id}
                  spy={true}
                  offset={offset}
                  smooth={true}
                  duration={scrollDuration}
                  className={`text-gray-300 hover:text-blue-400 font-bold px-3 py-1 transition 100
                  hover:cursor-pointer text-[0.75em] uppercase ${activeLink === id ? 'text-blue-400' : ''}`}
                  activeClass="text-blue-400"
                  onSetActive={() => setActiveLink(id)}
                  ref={ref}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`flex justify-center ${activeLink === id ? 'text-blue-400' : ''}`}> {icon} </span>
                    <span className={`${activeLink === id ? 'text-blue-400' : ''}`}> {title} </span>
                  </div>
                </Link>
              ))}

              <a
                href={downloadCvUrl}
                target="_blank"
                rel="noreferrer"
                className={`${cvButtonClass} hidden lg:inline-flex`}
                aria-label="Download Resume"
              >
                <CVIcon />
              </a>
            </div>
          </div>
          {/* Hamburger Menu Icon */}
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={toggleMenu}
              className="text-gray-300  hover:text-blue-400 inline-flex items-center justify-center p-2 rounded-md focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {links.map(({ id, title, offset, icon }) => (
                <Link
                  key={id}
                  to={id}
                  spy={true}
                  offset={offset}
                  smooth={true}
                  duration={scrollDuration}
                  onClick={() => setIsOpen(false)}
                  className="text-white flex hover:bg-black/75 hover:text-blue-400 hover:cursor-pointer py-2 rounded-sm text-base font-medium transition 100"
                >
                  <div className="flex space-x-3 px-2">
                    <span className="text-lg p-1"> {icon} </span>
                    <span className="text-[0.9em] m-2 uppercase tracking-widest"> {title} </span>
                  </div>
                </Link>
              ))}

              <a
                href={downloadCvUrl}
                target="_blank"
                rel="noreferrer"
                onClick={toggleMenu}
                className="mt-1 flex items-center justify-center gap-2 rounded-xl border border-cyan-400/70 bg-gradient-to-r from-blue-500/20 to-cyan-500/10 px-3 py-2.5 text-base font-medium text-blue-100 shadow-sm shadow-cyan-900/25 transition hover:bg-blue-500 hover:text-white"
              >
                <CVIcon />
                <span className="uppercase tracking-widest text-[0.85em]">Download Resume</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
